import React, { useState } from 'react';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';

import { DownloadSheet } from '../../../assets/svg/icons';
import { useCustomSelectors } from '../../../services/selectors/allSelectors';
import { useCustomLocation } from '../../../hooks';
import * as routeNames from '../../../routes/routeName';
import { getAppTimeLocalValue } from './DeliveredRxReasonChangeReviewShared';
import ReprocessedDeliveryReviewTable, {
	REPROCESSED_DELIVERY_SNOOZE_OPTIONS,
	useReprocessedDeliveryReviews,
} from './ReprocessedDeliveryReviewShared';

/**
 * The single popup for "this prescription was delivered, then reprocessed".
 *
 * THE REASON IS THE ONLY INPUT. There is no copy checkbox, no source picker and
 * no proof detail: the backend carries the existing delivery details across
 * automatically on submit. Skipping snoozes rather than dismisses, so an
 * unanswered item comes back - the same persistence model as the delivered-Rx
 * return-reason popup this is modelled on.
 */
const ReprocessedDeliveryReviewModal = ({
	records,
	totalCount,
	answers,
	reasonOptions,
	errors,
	submitting,
	exporting,
	onReasonChange,
	onExportCsv,
	onSubmit,
	onSkip,
}) => {
	const [snoozeOption, setSnoozeOption] = useState(REPROCESSED_DELIVERY_SNOOZE_OPTIONS[0].value);
	const [customSnoozeTime, setCustomSnoozeTime] = useState(() =>
		getAppTimeLocalValue(new Date(Date.now() + 60 * 60 * 1000))
	);
	const [isSkipMenuOpen, setIsSkipMenuOpen] = useState(false);

	if (!records.length) return null;

	const isCustomSnooze = snoozeOption === 'custom';
	const isSkipDisabled = submitting || exporting || (isCustomSnooze && !customSnoozeTime);
	const handleSkipClick = (snoozeConfig) => {
		onSkip({ minutes: snoozeConfig?.minutes, customTime: snoozeConfig?.customTime });
		setIsSkipMenuOpen(false);
	};

	return (
		<div className="fixed inset-0 z-[174] flex items-center justify-center bg-black/60 px-4 py-6">
			<div className="w-[96vw] max-w-[1360px] max-h-[90vh] overflow-hidden rounded-lg border border-slate-200 bg-white shadow-2xl">
				<div className="border-b border-slate-200 bg-slate-50 px-6 py-5">
					<div className="flex flex-wrap items-center justify-between gap-4">
						<div className="flex min-w-0 flex-wrap items-center gap-3">
							<span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-amber-100 text-amber-700">
								<ReportProblemOutlinedIcon fontSize="small" />
							</span>
							<h2 className="text-lg font-semibold text-slate-900">Reprocessed After Delivery</h2>
							{totalCount > 1 && (
								<span className="rounded-md bg-blue-100 px-2.5 py-1 text-xs font-semibold text-blue-800">
									{totalCount} pending
								</span>
							)}
						</div>
						<button
							type="button"
							onClick={onExportCsv}
							disabled={submitting || exporting}
							className={`flex h-10 items-center justify-center gap-2 rounded-md px-4 text-sm font-semibold text-white transition ${
								submitting || exporting ? 'cursor-not-allowed bg-slate-300' : 'bg-slate-700 hover:bg-slate-800'
							}`}
						>
							<DownloadSheet />
							{exporting ? 'Exporting...' : 'Export CSV'}
						</button>
					</div>
					<p className="mt-1 text-xs leading-5 text-slate-600">
						These prescriptions were delivered and then reprocessed in WinRx. Tell us why. The existing delivery
						details are carried over automatically, so no driver is sent out again for medicine the patient already has.
					</p>
				</div>

				<div className="max-h-[58vh] overflow-auto bg-white">
					<ReprocessedDeliveryReviewTable
						records={records}
						answers={answers}
						reasonOptions={reasonOptions}
						errors={errors}
						submitting={submitting}
						onReasonChange={onReasonChange}
					/>
				</div>

				<div className="relative z-30 flex flex-wrap items-center justify-end gap-3 border-t border-slate-200 bg-slate-50 px-6 py-4">
					<div className="relative">
						<button
							type="button"
							onClick={() => setIsSkipMenuOpen((isOpen) => !isOpen)}
							disabled={submitting || exporting}
							className={`relative z-10 border px-5 py-2 text-sm font-semibold transition ${
								submitting || exporting
									? 'cursor-not-allowed border-slate-200 bg-slate-100 text-slate-500'
									: 'border-slate-300 bg-white text-slate-700 hover:bg-slate-100'
							} ${isSkipMenuOpen ? 'rounded-b-md rounded-t-none border-t-white' : 'rounded-md'}`}
						>
							Skip
						</button>
						{isSkipMenuOpen && (
							<div className="absolute bottom-full right-0 z-50 -mb-px w-52 overflow-hidden rounded-md rounded-br-none border border-slate-300 bg-white shadow-lg">
								{REPROCESSED_DELIVERY_SNOOZE_OPTIONS.filter((option) => option.value !== 'custom').map((option) => (
									<button
										key={option.value}
										type="button"
										onClick={() => handleSkipClick({ minutes: option.minutes })}
									className="block w-full px-4 py-2 text-left text-sm font-medium text-slate-700 hover:bg-slate-50"
									>
										{option.label}
									</button>
								))}
								<div className="border-t border-slate-200 px-3 py-2">
									<button
										type="button"
										onClick={() => setSnoozeOption('custom')}
										className={`${snoozeOption === 'custom' ? 'mb-2' : ''} block w-full text-left text-sm font-semibold text-slate-700`}
									>
										Custom
									</button>
									{snoozeOption === 'custom' && (
										<div className="flex items-center gap-2">
											<input
												type="time"
												value={customSnoozeTime}
												onChange={(event) => setCustomSnoozeTime(event.target.value)}
											className="h-9 min-w-0 flex-1 rounded-md border border-slate-300 px-2 text-sm font-medium text-slate-800 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
											/>
											<button
												type="button"
												onClick={() => handleSkipClick({ customTime: customSnoozeTime })}
												disabled={isSkipDisabled}
												className={`h-9 rounded-md px-3 text-sm font-semibold transition ${
													isSkipDisabled
														? 'cursor-not-allowed bg-slate-100 text-slate-500'
														: 'bg-blue-600 text-white hover:bg-blue-700'
												}`}
											>
												Set
											</button>
										</div>
									)}
								</div>
							</div>
						)}
					</div>
					<button
						type="button"
						onClick={onSubmit}
						disabled={submitting || exporting}
						className={`rounded-md px-5 py-2 text-sm font-semibold transition ${
							submitting || exporting
								? 'cursor-not-allowed bg-slate-200 text-slate-600'
								: 'bg-blue-600 text-white hover:bg-blue-700'
						}`}
					>
						{submitting ? 'Saving...' : 'Continue'}
					</button>
				</div>
			</div>
		</div>
	);
};

const ReprocessedDeliveryReviewPopupManager = () => {
	const { pharmacyId, profileData, teamStatus } = useCustomSelectors();
	const location = useCustomLocation();
	const isPharmacist = String(profileData?.type || '').toLowerCase() === 'pharmacist';
	const isTeamUserRestricted = process.env.NODE_ENV === 'production' && Number(teamStatus) === 1;
	const isDispenseAttentionPage = location.pathname === routeNames.pharmacy_archived_deliveries;
	const isInvestigationPage = location.pathname === routeNames.pharmacy_investigate_rx;
	const isEnabled = Boolean(
		pharmacyId &&
			profileData?.user_id &&
			isPharmacist &&
			!isTeamUserRestricted &&
			!isDispenseAttentionPage &&
			!isInvestigationPage
	);

	const {
		records,
		answers,
		reasonOptions,
		fieldErrors,
		submitting,
		exporting,
		handleReasonChange,
		handleExportCsv,
		handleSubmit,
		handleSkip,
	} = useReprocessedDeliveryReviews({
		enabled: isEnabled,
		enableSnooze: true,
		pharmacyId,
		userId: profileData?.user_id,
	});

	if (!isEnabled) {
		return null;
	}

	return (
		<ReprocessedDeliveryReviewModal
			records={records}
			totalCount={records.length}
			answers={answers}
			reasonOptions={reasonOptions}
			errors={fieldErrors}
			submitting={submitting}
			exporting={exporting}
			onReasonChange={handleReasonChange}
			onExportCsv={handleExportCsv}
			onSubmit={handleSubmit}
			onSkip={handleSkip}
		/>
	);
};

export default ReprocessedDeliveryReviewPopupManager;
